import React from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './Legal.css';

const PrivacyPage = () => {
  const lastUpdated = 'July 20, 2025';

  return (
    <Container className="legal-page privacy-page">
      <Card className="legal-card">
        <Card.Header className="text-center">
          <h1>Privacy Policy</h1>
          <p className="text-muted">Last Updated: {lastUpdated}</p>
        </Card.Header>
        <Card.Body>
          <section className="mb-4">
            <h2>1. Introduction</h2>
            <p>
              At AffSimple.com, operated by Skjoldan Consulting (CVR: DK21417289), we respect your privacy and are committed to protecting your personal data.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service in compliance with the EU General Data Protection Regulation (GDPR) and Danish data protection laws.
            </p>
            <p>
              <strong>Data Controller:</strong> Skjoldan Consulting (CVR: DK21417289), Denmark<br />
              <strong>Contact:</strong> <a href="mailto:support@affsimple.com">support@affsimple.com</a>
            </p>
            <p>
              Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the service.
            </p>
          </section>

          <section className="mb-4">
            <h2>2. Information We Collect</h2>
            <p>
              <strong>Personal Data:</strong> While using our Service, we may ask you to provide certain personally identifiable information that can be used to contact or identify you.
              This includes:
            </p>
            <ul>
              <li>Email address</li>
              <li>First and last name</li>
              <li>Phone number</li>
              <li>Address, State, Province, ZIP/Postal code, City</li>
              <li>Payment information</li>
            </ul>
            <p>
              <strong>Usage Data:</strong> We may also collect information on how the Service is accessed and used. This includes:
            </p>
            <ul>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Time and date of your visit</li>
              <li>Time spent on pages</li>
              <li>Unique device identifiers</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2>3. How We Use Your Information</h2>
            <p>
              We use the information we collect for various purposes:
            </p>
            <ul>
              <li>To provide and maintain our Service</li>
              <li>To notify you about changes to our Service</li>
              <li>To allow you to participate in interactive features</li>
              <li>To provide customer support</li>
              <li>To gather analysis or valuable information to improve our Service</li>
              <li>To monitor the usage of our Service</li>
              <li>To detect, prevent and address technical issues</li>
              <li>To process transactions and send related information</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2>4. Legal Basis for Processing (GDPR Article 6)</h2>
            <p>
              We process your personal data based on the following legal grounds:
            </p>
            <ul>
              <li><strong>Contract Performance (Article 6(1)(b)):</strong> User registration, account management, service provision, subscription management</li>
              <li><strong>Legitimate Interest (Article 6(1)(f)):</strong> Click tracking, conversion tracking, analytics, fraud prevention, service improvement</li>
              <li><strong>Consent (Article 6(1)(a)):</strong> Marketing communications, non-essential cookies, newsletter subscriptions</li>
              <li><strong>Legal Obligation (Article 6(1)(c)):</strong> Tax records, accounting requirements, data subject rights fulfillment</li>
              <li><strong>Vital Interests (Article 6(1)(d)):</strong> Emergency situations requiring immediate action</li>
            </ul>
            <p>
              You have the right to object to processing based on legitimate interest. For consent-based processing, you may withdraw consent at any time.
            </p>
          </section>

          <section className="mb-4">
            <h2>5. Data Retention</h2>
            <p>
              We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy.
              We will retain and use your Personal Data to the extent necessary to comply with our legal obligations, resolve disputes,
              and enforce our legal agreements and policies.
            </p>
            <p>
              Usage Data is generally retained for a shorter period, except when this data is used to strengthen security or improve functionality,
              or we are legally obligated to retain this data for longer periods.
            </p>
          </section>

          <section className="mb-4">
            <h2>6. Data Transfer</h2>
            <p>
              Your information, including Personal Data, may be transferred to — and maintained on — computers located outside of your state,
              province, country or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
            </p>
            <p>
              If you are located outside the United States and choose to provide information to us, please note that we transfer the data,
              including Personal Data, to the United States and process it there.
            </p>
          </section>

          <section className="mb-4">
            <h2>7. Disclosure of Data</h2>
            <p>
              We may disclose your Personal Data in the good faith belief that such action is necessary to:
            </p>
            <ul>
              <li>Comply with a legal obligation</li>
              <li>Protect and defend the rights or property of Affiliate Tracking SaaS</li>
              <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
              <li>Protect the personal safety of users of the Service or the public</li>
              <li>Protect against legal liability</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2>8. Data Security</h2>
            <p>
              The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage
              is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="mb-4">
            <h2>9. Your Data Protection Rights</h2>
            <p>
              If you are a resident of the European Economic Area (EEA), you have certain data protection rights. We aim to take reasonable steps to allow
              you to correct, amend, delete, or limit the use of your Personal Data.
            </p>
            <p>
              If you wish to be informed about what Personal Data we hold about you and if you want it to be removed from our systems, please contact us.
            </p>
          </section>

          <section className="mb-4">
            <h2>10. Analytics</h2>
            <p>
              We may use third-party Service Providers to monitor and analyze the use of our Service.
            </p>
            <p>
              <strong>Google Analytics:</strong> Google Analytics is a web analytics service offered by Google that tracks and reports website traffic.
              Google uses the data collected to track and monitor the use of our Service. This data is shared with other Google services.
              You can opt-out of having your activity on the Service available to Google Analytics by installing the Google Analytics opt-out browser add-on.
            </p>
          </section>

          <section className="mb-4">
            <h2>11. Children's Privacy</h2>
            <p>
              Our Service does not address anyone under the age of 18 ("Children").
            </p>
            <p>
              We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware
              that your Child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without
              verification of parental consent, we take steps to remove that information from our servers.
            </p>
          </section>

          <section className="mb-4">
            <h2>12. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
            </p>
            <p>
              We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "last updated"
              date at the top of this Privacy Policy.
            </p>
          </section>

          <section className="mb-4">
            <h2>13. Your GDPR Rights</h2>
            <p>
              Under GDPR, you have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Right of Access:</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data</li>
              <li><strong>Right to Object:</strong> Object to processing of your data</li>
              <li><strong>Right to Restrict Processing:</strong> Request restriction of processing</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information below or use the data controls in your account settings.
            </p>
          </section>

          <section className="mb-4">
            <h2>14. Data Retention</h2>
            <p>
              We retain your personal data for as long as necessary to provide our services and comply with legal obligations:
            </p>
            <ul>
              <li><strong>Account data:</strong> Until account deletion + 30-day grace period, then permanently deleted</li>
              <li><strong>Financial records:</strong> 5 years after transaction (Danish Bookkeeping Act requirement)</li>
              <li><strong>Click tracking data:</strong> 2 years from collection (legitimate interest basis)</li>
              <li><strong>Conversion data:</strong> 7 years (tax and accounting requirements)</li>
              <li><strong>Marketing data:</strong> Until consent withdrawal or 3 years of inactivity</li>
              <li><strong>Analytics data:</strong> 26 months (Google Analytics default, anonymized after 14 months)</li>
              <li><strong>Support communications:</strong> 3 years after resolution</li>
            </ul>
          </section>

          <section className="mb-4">
            <h2>15. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or wish to exercise your GDPR rights, please contact us:
            </p>
            <p>
              <strong>Data Controller:</strong><br />
              Skjoldan Consulting<br />
              CVR: DK21417289<br />
              Address: Denmark<br />
              Email: <a href="mailto:support@affsimple.com">support@affsimple.com</a><br />
              Website: <a href="https://affsimple.com">https://affsimple.com</a>
            </p>
            <p>
              <strong>Supervisory Authority:</strong><br />
              Danish Data Protection Authority (Datatilsynet)<br />
              Website: <a href="https://www.datatilsynet.dk">www.datatilsynet.dk</a><br />
              Email: <a href="mailto:dt@datatilsynet.dk">dt@datatilsynet.dk</a>
            </p>
          </section>
        </Card.Body>
        <Card.Footer className="text-center">
          <Button as={Link} to="/" variant="primary" className="me-2">
            Return to Home
          </Button>
          <Button as={Link} to="/terms" variant="outline-primary">
            Terms and Conditions
          </Button>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default PrivacyPage;
